import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useAuth from '../Hooks/useAuth';
import { addTorkOrders, updateTorkOrder, removeTorkOrder } from '../../state/slices/TorkOrderSlice';
import { addTork, updateTork, removeTork } from '../../state/slices/TorkSlice';
import { NotifyInfo } from '../Notify/notify';

// REACT_APP_API_BASE_URL ends in /api; SignalR hubs are mapped at the root level
const HUB_URL = process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '') + '/hubs/tork';

const useTorkHub = () => {
    const { jwtToken, email: currentUserEmail, hasTorkAccess } = useAuth();
    const dispatch = useDispatch();
    const connectionRef = useRef(null);
    const location = useLocation();

    const notify = (msg) => {
        if (location.pathname === '/torkplanering') NotifyInfo(msg);
    };

    useEffect(() => {
        if (!jwtToken) return;
        if (location.pathname !== '/torkplanering') return;
        if (!hasTorkAccess()) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, { accessTokenFactory: () => jwtToken })
            .withAutomaticReconnect()
            .build();

        const isOtherUser = (email) => email !== currentUserEmail;

        connection.on('TorkOrdersAdded', ({ orders, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(addTorkOrders(orders));
                notify(`[${triggeredByEmail}] - Skapade en torkorder`);
            }
        });

        connection.on('TorkOrderUpdated', ({ order, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(updateTorkOrder(order));
                notify(`[${triggeredByEmail}] - Uppdaterade en torkorder`);
            }
        });

        connection.on('TorkOrderRemoved', ({ orderId, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(removeTorkOrder({ id: orderId }));
                notify(`[${triggeredByEmail}] - Tog bort en torkorder`);
            }
        });

        connection.on('TorkOrdersPriosUpdated', ({ orders, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                orders.forEach(order => dispatch(updateTorkOrder(order)));
                notify(`[${triggeredByEmail}] - Uppdaterade prioritetsordning`);
            }
        });

        connection.on('TorkAdded', ({ tork, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(addTork(tork));
                notify(`[${triggeredByEmail}] - La till en tork`);
            }
        });

        connection.on('TorkUpdated', ({ tork, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(updateTork(tork));
                notify(`[${triggeredByEmail}] - Uppdaterade en tork`);
            }
        });

        connection.on('TorkRemoved', ({ torkId, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(removeTork({ id: torkId }));
                notify(`[${triggeredByEmail}] - Tog bort en tork`);
            }
        });

        connection.start().catch(err => console.error('SignalR (tork) connection error:', err));
        connectionRef.current = connection;

        return () => {
            connection.stop();
        };
    // Re-run when navigating to/from the tork planning page or when jwtToken refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwtToken, location.pathname]);

    return null;
};

export default useTorkHub;
